import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectAuth } from "../../redux/features/auth/auth-slice";
import { selectIsDarkMode } from "../../redux/features/theme/theme-slice";
import rightSideImage from "../../shared/oversight-core/assets/images/login_pg.svg";
import { EApplicationType } from "../../shared/oversight-core/enums/application-type";
import { EInverterPhaseType } from "../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSystemType } from "../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../shared/oversight-core/enums/utility-option-type";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import AppSelect, {
  Option,
} from "../../shared/oversight-core/ui-elements/app-select/app-select";
import ArrowButton from "../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import AppButton from "../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppInput from "../../shared/oversight-core/ui-elements/input/app-input";
import { MtIcon } from "../../shared/oversight-core/ui-elements/material-icon/material-icon";
import RangeSlider from "../../shared/oversight-core/ui-elements/range-slider/range-slider";
import StepsIcon from "../../shared/oversight-core/ui-elements/steps-icon/steps-icon";
import SolarSolutionType from "../build-system/components/solar-solution-type/solar-solution-type";
import SolarSystemType from "../build-system/components/solar-system-type/solar-system-type";
import CalculateEnergyModal from "./calculate-energy-modal/calculate-energy-modal";
import styles from "./calculate-energy.module.scss";

const solarSolutionTypes = [
  {
    id: 1,
    icon: "solar_power" as MtIcon,
    title: "Build Solar Energy System Only",
    solarSolutionType: ESolarSolutionType.SOLAR_ENERGY_SYSTEM,
  },
  {
    id: 2,
    icon: "cool_to_dry" as MtIcon,
    title: "Build a Water Heater System",
    solarSolutionType: ESolarSolutionType.WATER_HEATING_SYSTEM,
  },
  {
    id: 3,
    icon: "solar_power" as MtIcon,
    icon2: "cool_to_dry" as MtIcon,
    title: "Build Solar Energy & Water Heating System",
    solarSolutionType: ESolarSolutionType.BOTH,
  },
];

const solarSystemTypes = [
  {
    id: 1,
    icon: "battery_full" as MtIcon,
    title: "Backup",
    description: "For grid-connected setups that run only during power cuts.",
    solarSystemType: ESolarSystemType.BACKUP,
  },
  {
    id: 2,
    icon: "electric_meter" as MtIcon,
    title: "Off-Grid",
    description:
      "Completely independent from the grid, stores solar power in batteries for 24/7 use.",
    solarSystemType: ESolarSystemType.OFF_GRID,
  },
  {
    id: 3,
    icon: "water_ec" as MtIcon,
    title: "Hybrid",
    description:
      "Smart mix of solar, grid, and battery, automatically switches for best efficiency.",
    solarSystemType: ESolarSystemType.HYBRID,
  },
];

const solarSystemModes = [
  {
    id: 1,
    title: "I Know My Energy Needs",
    description:
      "Enter your details to quickly calculate your solar system size and cost estimate.",
  },
  {
    id: 2,
    title: "Find My Energy Needs",
    description:
      "We’ll guide you step by step to understand your energy usage and get a more accurate result.",
  },
];

const solarSystemTypeOptions: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.INSTITUTIONS,
    label: "Institutions",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None (No grid connection)",
  },
];

const percentageOptions: Option[] = Array.from({ length: 100 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1}%`,
}));

interface IFormInput {
  monthlyEnergyNeed: string;
  maxPowerNeed: string;
  nightLoad: Option | [];
  numberOfUsers: number | undefined;
}

const defaultFormValues: IFormInput = {
  monthlyEnergyNeed: "",
  maxPowerNeed: "",
  nightLoad: {
    value: "60",
    label: "60%",
  },
  numberOfUsers: undefined,
};

interface ILocationState {
  step: number;
  numberOfUsers: number;
  utilityOption: EUtilityOptionType;
  applicationType: EApplicationType;
  institution: EApplicationType;
  estimationId: string;
  estimationToken: string;
}

const CalculateEnergy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const isDarkMode = useSelector(selectIsDarkMode);
  const locationState = location.state as ILocationState;

  const [showCalculateEnergyModal, setShowCalculateEnergyModal] =
    useState(false);
  const [selectedSolarSolutionType, setSelectedSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);
  const [selectedSolarSystemType, setSelectedSolarSystemType] =
    useState<Option>({ ...solarSystemTypeOptions[2] });
  const [step, setStep] = useState(1);
  const [selectSolarSystemMode, setSelectSolarSystemMode] = useState(1);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [selectedApplicationType, setSelectedApplicationType] =
    useState<Option>({ ...applicationTypeOptions[0] });
  const [selectedInstitution, setSelectedInstitution] = useState<Option>({
    ...applicationTypeOptions[2],
  });
  const [selectedUtilityOption, setSelectedUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [iconStep, setIconStep] = useState(1);
  const [selectedInverterPhaseType, setSelectedInverterPhaseType] =
    useState<Option>({ ...inverterPhaseTypeOptions[0] });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const numberOfUsers = watch("numberOfUsers");

  const onSubmit = (data: IFormInput) => {
    const params = {
      systemType: selectedSolarSystemType.value as ESolarSystemType,
      monthlyEnergyNeed: Number(data.monthlyEnergyNeed),
      maxPowerNeed: Number(data.maxPowerNeed),
      nightLoad: Number((data.nightLoad as Option).value),
      sunlightHours: sunlightHours,
      numberOfUsers: data.numberOfUsers ?? 0,
      applicationType:
        selectedApplicationType.value === EApplicationType.RESIDENTIAL
          ? (selectedApplicationType.value as EApplicationType)
          : (selectedInstitution.value as EApplicationType),
      utilityOption: selectedUtilityOption.value as EUtilityOptionType,
      solarSolutionType: selectedSolarSolutionType,
      inverterPhaseType: selectedInverterPhaseType.value as EInverterPhaseType,
      estimationId: locationState?.estimationId,
      estimationToken: locationState?.estimationToken,
    };

    if (auth.token) {
      navigate(AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM, {
        state: {
          ...params,
        },
      });
      return;
    }

    navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM, {
      state: {
        ...params,
      },
    });
  };

  const onNextSubmit = handleSubmit(async (data) => {
    setStep(2);
    setIconStep(2);

    if (step === 4) {
      await onSubmit(data);
    }
  });

  const onSubmitOnlyWaterHeaterSystem = handleSubmit(async (data) => {
    const params = {
      numberOfUsers: data.numberOfUsers,
      applicationType:
        selectedApplicationType.value === EApplicationType.RESIDENTIAL
          ? (selectedApplicationType.value as EApplicationType)
          : (selectedInstitution.value as EApplicationType),
      utilityOption: selectedUtilityOption.value as EUtilityOptionType,
      solarSolutionType: selectedSolarSolutionType,
    };

    if (auth.token) {
      navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM, {
        state: {
          ...params,
        },
      });
      return;
    }

    navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM, {
      state: {
        ...params,
      },
    });
  });

  useEffect(() => {
    reset();
  }, [selectedSolarSolutionType]);

  useEffect(() => {
    if (locationState) {
      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === locationState.utilityOption
      );
      const selectedInstitutionType = applicationTypeOptions.find(
        (i) => i.value === locationState.institution
      );
      setStep(locationState.step);
      setIconStep(locationState.step);
      setValue("numberOfUsers", locationState.numberOfUsers);

      if (locationState.applicationType === EApplicationType.RESIDENTIAL) {
        setSelectedApplicationType({ ...applicationTypeOptions[0] });
      } else {
        setSelectedApplicationType({ ...applicationTypeOptions[1] });
      }

      if (
        selectedInstitutionType &&
        locationState.applicationType !== EApplicationType.RESIDENTIAL
      ) {
        setSelectedInstitution({
          value: selectedInstitutionType.value,
          label: selectedInstitutionType.label,
        });
      }

      if (selectedUtilityOption) {
        setSelectedUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }
    }
  }, [locationState]);

  return (
    <>
      <Container className="py-5">
        <Row className="align-items-center justify-content-center gap-5">
          <Col className="col-12 col-lg-10 col-xl-8 col-xxl-7">
            <Row className="gx-0">
              <Col className="col-12 col-md-5">
                <div
                  className={`${styles.leftSideContainerOne} ${
                    isDarkMode
                      ? styles.leftSideContainerOneDark
                      : styles.leftSideContainerOneLight
                  } p-3`}
                >
                  <Row>
                    <Col className="mx-3 title-1 font-weight-500 font-size-32 text-center">
                      Find Your Solar Solution
                    </Col>
                  </Row>
                  <Row className="mb-5">
                    <Col className="text-advance-description font-weight-400 font-size-16">
                      Select the solar solution type.
                    </Col>
                  </Row>
                  {solarSolutionTypes.map((types) => {
                    return (
                      <Row key={types.id} className="my-2">
                        <Col>
                          <SolarSolutionType
                            item={types}
                            onSelect={() => {
                              setSelectedSolarSolutionType(
                                types.solarSolutionType
                              );
                              setStep(1);
                            }}
                            isSelected={
                              selectedSolarSolutionType ===
                              types.solarSolutionType
                            }
                            isDarkMode={isDarkMode}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              </Col>
              <Col>
                <div
                  className={`${styles.leftSideContainerTwo}  ${
                    isDarkMode
                      ? styles.leftSideContainerTwoDark
                      : styles.leftSideContainerTwoLight
                  } p-4`}
                >
                  <Row className="mx-0 justify-content-end">
                    {step > 1 && (
                      <Col className="mb-5">
                        <ArrowButton
                          onBack={() => {
                            if (
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.SOLAR_ENERGY_SYSTEM &&
                                step === 2) ||
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                                step === 2)
                            ) {
                              setStep(1);
                              setIconStep(1);
                              return;
                            }

                            if (
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.SOLAR_ENERGY_SYSTEM &&
                                step === 3) ||
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                                step === 3)
                            ) {
                              setStep(2);
                              return;
                            }

                            if (
                              selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                              step === 4
                            ) {
                              setStep(3);
                            }
                          }}
                        />
                      </Col>
                    )}
                    {selectedSolarSolutionType === ESolarSolutionType.BOTH && (
                      <Col className="col-auto mb-5">
                        <StepsIcon
                          steps={[
                            {
                              id: 1,
                              stepName: "Water Heater",
                              stepIcon: "water_drop",
                            },
                            {
                              id: 2,
                              stepName: "Solar Energy",
                              stepIcon: "solar_power",
                            },
                          ]}
                          selectedStep={iconStep}
                          isDarkMode={isDarkMode}
                        />
                      </Col>
                    )}
                  </Row>
                  {((step === 1 &&
                    selectedSolarSolutionType === ESolarSolutionType.BOTH) ||
                    (step === 1 &&
                      selectedSolarSolutionType ===
                        ESolarSolutionType.WATER_HEATING_SYSTEM)) && (
                    <>
                      <AppSelect
                        label="Application Type"
                        selectedValue={selectedApplicationType}
                        options={[...applicationTypeOptions.slice(0, 2)]}
                        onChangeOption={(selectOption) => {
                          setSelectedApplicationType(selectOption);
                        }}
                        isRequired
                      />
                      {selectedApplicationType.value ===
                        EApplicationType.INSTITUTIONS && (
                        <AppSelect
                          className="mt-4"
                          label="Institution"
                          selectedValue={selectedInstitution}
                          options={[...applicationTypeOptions.slice(2)]}
                          onChangeOption={(selectOption) => {
                            setSelectedInstitution(selectOption);
                          }}
                          isRequired
                        />
                      )}
                      <AppSelect
                        className="mt-4"
                        label="Utility Option"
                        selectedValue={selectedUtilityOption}
                        options={[...utilityOptions]}
                        onChangeOption={(selectOption) => {
                          setSelectedUtilityOption(selectOption);
                        }}
                        isRequired
                      />
                      <AppInput
                        className="mt-4"
                        label="Number of Users"
                        placeholder="Number of Users"
                        type="number"
                        name="numberOfUsers"
                        register={register("numberOfUsers", {
                          required: "Number of users is required.",
                          min: {
                            value: 1,
                            message: "Value should be greater than 0",
                          },
                        })}
                        onKeyDown={(e) => {
                          if (e.key === "." || e.key === "," || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        errors={errors}
                      />
                      {selectedSolarSolutionType !==
                        ESolarSolutionType.BOTH && (
                        <Row className="mt-4">
                          <Col>
                            <AppButton
                              text="Design Your System"
                              variant="blue"
                              className="font-weight-600"
                              onClick={onSubmitOnlyWaterHeaterSystem}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  {((step === 2 &&
                    selectedSolarSolutionType === ESolarSolutionType.BOTH) ||
                    (step === 1 &&
                      selectedSolarSolutionType ===
                        ESolarSolutionType.SOLAR_ENERGY_SYSTEM)) && (
                    <>
                      <Row className="mb-3">
                        <Col className="solution-type-text font-weight-500 font-size-14">
                          Select the solar solution type
                        </Col>
                      </Row>
                      {solarSystemTypes.map((types) => {
                        return (
                          <Row key={types.id} className="mb-3">
                            <Col>
                              <SolarSystemType
                                item={types}
                                onSelect={() => {
                                  const selectedTypeOption =
                                    solarSystemTypeOptions.find(
                                      (s) => s.value === types.solarSystemType
                                    );
                                  if (selectedTypeOption) {
                                    setSelectedSolarSystemType({
                                      value: selectedTypeOption.value,
                                      label: selectedTypeOption.label,
                                    });
                                  }
                                }}
                                isSelected={
                                  selectedSolarSystemType.value ===
                                  types.solarSystemType
                                }
                                isDarkMode={isDarkMode}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                      {selectedSolarSystemType.value !==
                        ESolarSystemType.OFF_GRID && (
                        <Row>
                          <Col>
                            <AppSelect
                              label="Grid Type"
                              selectedValue={selectedInverterPhaseType}
                              options={[...inverterPhaseTypeOptions]}
                              onChangeOption={(selectOption) => {
                                setSelectedInverterPhaseType(selectOption);
                              }}
                              isRequired
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  {((step === 2 &&
                    selectedSolarSolutionType ===
                      ESolarSolutionType.SOLAR_ENERGY_SYSTEM) ||
                    (step === 3 &&
                      selectedSolarSolutionType ===
                        ESolarSolutionType.BOTH)) && (
                    <>
                      <Row>
                        <Col>
                          <AppSelect
                            label="Solar Solution Type"
                            selectedValue={selectedSolarSystemType}
                            options={[...solarSystemTypeOptions]}
                            onChangeOption={(selectOption) => {
                              setSelectedSolarSystemType(selectOption);
                            }}
                            isRequired
                          />
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col className="solar-system-type-text font-weight-500 font-size-14">
                          Select the Mode
                        </Col>
                      </Row>
                      {solarSystemModes.map((mode) => {
                        return (
                          <Row key={mode.id} className="my-2">
                            <Col>
                              <SolarSystemType
                                item={mode}
                                onSelect={() =>
                                  setSelectSolarSystemMode(mode.id)
                                }
                                isSelected={selectSolarSystemMode === mode.id}
                                isDarkMode={isDarkMode}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                    </>
                  )}
                  {((step === 3 &&
                    selectedSolarSolutionType ===
                      ESolarSolutionType.SOLAR_ENERGY_SYSTEM) ||
                    (step === 4 &&
                      selectedSolarSolutionType ===
                        ESolarSolutionType.BOTH)) && (
                    <>
                      <Row>
                        <Col>
                          <AppSelect
                            label="Solar Solution Type"
                            selectedValue={selectedSolarSystemType}
                            options={[...solarSystemTypeOptions]}
                            onChangeOption={(selectOption) => {
                              setSelectedSolarSystemType(selectOption);
                            }}
                            isRequired
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <AppInput
                            className="mt-4"
                            label="Monthly Energy Need"
                            placeholder="Monthly Energy Need"
                            type="number"
                            name="monthlyEnergyNeed"
                            register={register("monthlyEnergyNeed", {
                              required: "Monthly energy need is required.",
                              min: {
                                value: 1,
                                message: "Value should be greater than 0",
                              },
                            })}
                            rightSideText="Units"
                            errors={errors}
                            isShowInfoIcon
                            info="Total energy consumed over a month, shown in utility bill as units/kWh."
                          />
                          <AppInput
                            className="mt-4"
                            label="Max Power Need"
                            placeholder="Max Power Need"
                            type="number"
                            register={register("maxPowerNeed", {
                              required: "Max power need is required ",
                              min: {
                                value: 0.1,
                                message: "Value should be greater than 0",
                              },
                            })}
                            errors={errors}
                            name="maxPowerNeed"
                            rightSideText="kW"
                            isShowInfoIcon
                            info="Peak power usage if all major devices run at once."
                          />
                          <AppSelect
                            className="mt-4"
                            placeholder="Night Load"
                            selectedValue={undefined}
                            options={percentageOptions}
                            label="Night Load"
                            control={control}
                            name="nightLoad"
                            errors={errors}
                            register={register("nightLoad", {
                              required: "Please select a value",
                            })}
                            menuHeight="200px"
                            isRequired
                            isShowInfoIcon
                            info="Approximate portion of your energy usage that occurs at night."
                          />
                          <Row className="mt-3">
                            <Col>
                              <RangeSlider
                                label="Daily Sunlight"
                                unit="hours"
                                min={4}
                                max={6}
                                step={0.5}
                                defaultValue={sunlightHours}
                                onChange={(value) => setSunlightHours(value)}
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col>
                          <AppButton
                            text="Design Your System"
                            variant="blue"
                            className="font-weight-600"
                            onClick={handleSubmit(onSubmit)}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  {(step === 3 &&
                    selectedSolarSolutionType ===
                      ESolarSolutionType.SOLAR_ENERGY_SYSTEM) ||
                  selectedSolarSolutionType ===
                    ESolarSolutionType.WATER_HEATING_SYSTEM ||
                  (step === 4 &&
                    selectedSolarSolutionType === ESolarSolutionType.BOTH) ? (
                    <></>
                  ) : (
                    <Row className="mt-4">
                      <Col>
                        <AppButton
                          text="Next"
                          variant="blue"
                          className="font-weight-600"
                          onClick={() => {
                            if (
                              selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                              step === 1
                            ) {
                              onNextSubmit();
                              return;
                            }

                            if (
                              selectedSolarSolutionType ===
                                ESolarSolutionType.SOLAR_ENERGY_SYSTEM &&
                              step === 1
                            ) {
                              setStep(2);
                              return;
                            }

                            if (
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.SOLAR_ENERGY_SYSTEM &&
                                selectSolarSystemMode === 1) ||
                              (selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                                step === 2)
                            ) {
                              setStep(3);
                              return;
                            }

                            if (
                              selectedSolarSolutionType ===
                                ESolarSolutionType.BOTH &&
                              step === 3 &&
                              selectSolarSystemMode === 1
                            ) {
                              setStep(4);
                              return;
                            }

                            if (selectSolarSystemMode === 2) {
                              setShowCalculateEnergyModal(true);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
          <Col className="col-5 col-xxl-4 d-none d-xl-block">
            <img
              src={rightSideImage}
              alt="Login illustration"
              className="w-100"
            />
          </Col>
        </Row>
      </Container>
      <CalculateEnergyModal
        show={showCalculateEnergyModal}
        onClose={() => setShowCalculateEnergyModal(false)}
        numberOfUsers={numberOfUsers ?? 0}
        applicationType={
          selectedApplicationType.value === EApplicationType.RESIDENTIAL
            ? (selectedApplicationType.value as EApplicationType)
            : (selectedInstitution.value as EApplicationType)
        }
        utilityOption={selectedUtilityOption.value as EUtilityOptionType}
        solarSolutionType={selectedSolarSolutionType}
        systemType={selectedSolarSystemType.value as ESolarSystemType}
        inverterPhaseType={
          selectedInverterPhaseType.value as EInverterPhaseType
        }
        estimationId={locationState?.estimationId}
        estimationToken={locationState?.estimationToken}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default CalculateEnergy;
